import React, { useEffect, useState } from "react";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import { Alert, Button, Checkbox, Divider, Input, Result } from "antd";
import { GoogleOutlined } from "@ant-design/icons";

/**
 * Public "Delete my account" page (/delete-account) — works without logging
 * in; this is the account-deletion URL listed on Google Play.
 *
 * The person enters their Gmail, then proves they own it by signing in with
 * that same Google account (or, for email/password signups, their password).
 *
 * Calls the backend with plain axios on purpose: the shared `api` client
 * treats any 401 as an expired session and logs the browser out, but here a
 * 401 just means "wrong password".
 */
const API_URL = process.env.REACT_APP_BASE_API_URL1;

const clearLocalSession = () => {
  ["token", "refreshToken", "user"].forEach((k) => localStorage.removeItem(k));
};

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready = emailValid && confirmed && !loading;

  const submit = async (proof) => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API_URL}/auth/delete-account`, {
        email: email.trim().toLowerCase(),
        ...proof,
      });
      clearLocalSession();
      setDeleted(true);
    } catch (err) {
      setError(
        err.response?.status === 429
          ? "Too many attempts. Please try again in 15 minutes."
          : err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyWithGoogle = useGoogleLogin({
    onSuccess: (tokenResponse) => submit({ googleToken: tokenResponse.access_token }),
    onError: () => setError("Google sign-in was cancelled or failed. Please try again."),
    uxMode: "popup",
  });

  if (deleted) {
    return (
      <div className="page-content">
        <div className="container py-5" style={{ maxWidth: 640 }}>
          <Result
            status="success"
            title="Your account has been deleted"
            subTitle="Your Websamp account and personal data have been permanently removed. You have been signed out on this browser. Please also sign out of the Websamp app on your phone."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container privacy-wrapper text-start py-5" style={{ maxWidth: 640 }}>
        <h1 className="text-start">Delete your account</h1>
        <p>
          You can permanently delete your Websamp account here — from the website or the
          app, no login needed.
        </p>

        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="This cannot be undone"
          description={
            <ul className="mb-0 ps-3">
              <li>Your profile, survey profile answers and devices are permanently erased.</li>
              <li>Any unredeemed points in your wallet are forfeited.</li>
              <li>
                Records of past earnings, withdrawals and referrals are kept in anonymised form
                where required for accounting and legal reasons.
              </li>
              <li>You can sign up again later, but as a brand-new account.</li>
            </ul>
          }
        />

        <label htmlFor="delete-email" className="form-label fw-semibold">
          Your account email (Gmail)
        </label>
        <Input
          id="delete-email"
          size="large"
          type="email"
          autoComplete="email"
          placeholder="you@gmail.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
          }}
          status={email && !emailValid ? "error" : ""}
        />
        {email && !emailValid && (
          <div className="text-danger small mt-1">Please enter a valid email address.</div>
        )}

        <Checkbox
          className="mt-4"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        >
          I understand my account and data will be permanently deleted.
        </Checkbox>

        {error && <Alert className="mt-4" type="error" showIcon message={error} />}

        <div className="mt-4">
          {!usePassword ? (
            <>
              <p className="text-muted small mb-2">
                To confirm it's you, sign in with the same Google account.
              </p>
              <Button
                danger
                type="primary"
                size="large"
                block
                icon={<GoogleOutlined />}
                disabled={!ready}
                loading={loading}
                onClick={() => verifyWithGoogle()}
              >
                Verify with Google &amp; delete account
              </Button>
              <Divider plain>or</Divider>
              <Button type="link" block onClick={() => setUsePassword(true)}>
                I signed up with email and password
              </Button>
            </>
          ) : (
            <>
              <label htmlFor="delete-password" className="form-label fw-semibold">
                Password
              </label>
              <Input.Password
                id="delete-password"
                size="large"
                autoComplete="current-password"
                placeholder="Your Websamp password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                onPressEnter={() => ready && password && submit({ password })}
              />
              <Button
                className="mt-3"
                danger
                type="primary"
                size="large"
                block
                disabled={!ready || !password}
                loading={loading}
                onClick={() => submit({ password })}
              >
                Delete my account
              </Button>
              <Button type="link" block className="mt-2" onClick={() => setUsePassword(false)}>
                Verify with Google instead
              </Button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
